import { createContext } from "react";
import type { NavigateFunction } from "react-router";
import type { ArgusInstanceStatus } from "../types/argus";
import type { ArgusGatewayEvent, ArgusMessage } from "../types/argus-events";

/**
 * Argus connection context — UI state only.
 *
 * Follows the same split as `SupportChat*`: the context holds shared UI state,
 * while the internal hook (`use-argus-connection.ts`) owns the SSE connection,
 * gateway client, event buffer, and mutation calls.
 *
 * Consumers must not reach into SSE internals directly — go through the
 * provided `sendMessage` / `startSession` / `stopSession` / `answerQuestion`
 * methods so the hook can keep a single source of truth for the event stream.
 *
 * Phase 1 scope: the provider mounts with a stub implementation of the hook
 * that leaves most fields idle (`isConnected: false`, empty buffers). Phase 2
 * wires the real `useArgusSSE` / `useArgusGateway` hooks behind this interface.
 */

/**
 * A pending `ask_user` question, populated by scanning `tool_execution_*`
 * events in Phase 4a. Phase 1 defines the shape so downstream files type-check.
 */
export interface PendingQuestion {
  toolCallId: string;
  sessionId: string;
  // The raw args payload the pi tool received — shape matches the TypeBox
  // schema in `be/argus-ts/src/extensions/ask-user.ts` (Phase 4a). Using
  // `unknown` here to avoid leaking the schema type across the module boundary.
  questions: unknown;
  createdAt: number;
}

export interface ArgusConnectionContextValue {
  // --- Sidebar UI state (Phase 3 wires this) ---
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // --- Active session ---
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;

  /**
   * While a new session is bootstrapping (first `sendMessage` → gateway
   * `startSession` round-trip), this holds a preview of the outgoing message
   * so the session list can render a placeholder "sending…" row instead of
   * leaving the user staring at an empty selection. Cleared as soon as the
   * real `activeSessionId` is set (or the send errors out).
   */
  pendingSessionPreview: string | null;

  /**
   * User-authored messages shown optimistically in the chat transcript the
   * moment they're sent. Pi-coding-agent only emits assistant-side events,
   * so without this list the user's own text would stay invisible until
   * the next transcript reload. Cleared on `activeSessionId` change.
   */
  optimisticUserMessages: string[];

  /**
   * True from `sendMessage` until the first assistant event arrives for the
   * turn. Drives the ephemeral "Argus is thinking" pill in the transcript
   * so the user sees feedback during the silent pre-stream gap.
   */
  isAwaitingResponse: boolean;

  /**
   * True while the per-session transcript fetch is in flight on session
   * switch. Drives the "Loading conversation…" state in the transcript area
   * so the user knows something is happening.
   */
  isTranscriptLoading: boolean;

  /**
   * True when the active session's transcript was served from the archive
   * because the live gateway was unavailable (instance gone/paused/updated).
   * The UI renders a "This session has ended." banner above the transcript
   * and disables the composer. Never exposes any internal terminology
   * ("S3", "sandbox", "backup", "archived") in the user-visible string.
   */
  isReadOnly: boolean;

  // --- Instance + events (populated by the internal hook) ---
  instanceStatus: ArgusInstanceStatus | null;
  events: ArgusGatewayEvent[];
  seededMessages: ArgusMessage[];

  /**
   * Tool call IDs that the gateway reported as still in-flight at the
   * moment the active session was hydrated (one-shot read on session
   * switch). The message list uses this to keep tool blocks marked
   * RUNNING when the seeded transcript would otherwise mark them DONE
   * because no `tool_execution_end` event made it into the seed.
   */
  hydratedOpenToolCallIds: readonly string[];

  // --- Connection state (mirrors `useArgusSSE`) ---
  isConnected: boolean;
  connectionError: string | null;

  // --- Agent working state ---
  /**
   * True when the agent has an open turn (turn_start without a matching
   * turn_end). Used to switch the input between send and queue modes and
   * to show the stop button.
   */
  isAgentWorking: boolean;

  /**
   * True after the user clicks Stop, until the next turn_start. Drives
   * the "Session stopped" indicator so the user knows the abort landed.
   */
  wasStopped: boolean;

  // --- Message queue (Phase 6 — backend-managed via pi) ---
  /**
   * Live queue state from pi-coding-agent, updated via `queue_update` SSE
   * events. `steering` messages interrupt the agent's current turn;
   * `followUp` messages wait until the agent finishes.
   */
  queuedMessages: { steering: readonly string[]; followUp: readonly string[] };
  /** Clear all queued messages (both steering and followUp). */
  clearQueue: () => Promise<void>;
  /** Remove a single queued message by type and index. */
  removeFromQueue: (type: "steering" | "followUp", index: number) => Promise<void>;
  /**
   * Promote a queued message to an immediate steer. Removes the original
   * from the queue and re-sends `text` via `sendMessage(text, "steer")` so
   * pi injects it into the running turn. `text` should be the cleaned
   * display body (sender block + context prefix stripped) — the gateway
   * re-prepends both on the new send.
   */
  promoteToSteer: (type: "steering" | "followUp", index: number, text: string) => Promise<void>;

  // --- Gateway actions (wraps `useArgusGateway`) ---
  sendMessage: (message: string, method?: "steer" | "followUp") => Promise<void>;
  startSession: (prompt: string) => Promise<string>;
  stopSession: () => Promise<void>;

  // --- Pending `ask_user` questions (Phase 4a) ---
  pendingQuestions: PendingQuestion[];
  answerQuestion: (toolCallId: string, answers: unknown[]) => Promise<void>;
  dismissQuestion: (toolCallId: string) => Promise<void>;

  // --- Navigate bridge (Phase 4b) ---
  //
  // React Router's `useNavigate()` hook can only be called inside the
  // router tree. The provider mounts inside the router context today but
  // we keep this indirection so a future refactor that moves the provider
  // above the router doesn't break auto-navigate. `<ArgusNavigateBridge />`
  // calls `useNavigate()` and registers the function; the provider's
  // `tool_execution_end` handler reads the latest value via a ref.
  registerNavigate: (fn: NavigateFunction | null) => void;
}

export const ArgusConnectionContext = createContext<ArgusConnectionContextValue | undefined>(
  undefined,
);
